import React, { useEffect, useState } from 'react';
import { api } from '../api/client';
import HistoryImport from '../components/HistoryImport';
import TransactionImport, { TransactionBatchView } from '../components/TransactionImport';
import useRefreshOnFocus from '../useRefreshOnFocus';

const TYPE_LABELS = { security: 'Prices', money_market: 'Money market yield', transactions: 'Transactions' };
const SOURCE_LABELS = { upload: 'Upload', drop_folder: 'Drop folder' };
const OUTCOMES = [
  { value: 'loaded', label: 'Loaded', cls: 'gain' },
  { value: 'loaded_errors', label: 'Loaded with errors', cls: 'loss' },
  { value: 'staged', label: 'Waiting to load', cls: '' },
  { value: 'failed', label: 'Failed', cls: 'loss' },
  { value: 'discarded', label: 'Discarded', cls: 'muted' },
];
const STATUS_LABELS = { invalid: 'Invalid', partial_day: 'Partial day', duplicate: 'Duplicate', skipped: 'Skipped' };

// A loaded import that left invalid rows out is its own outcome, matching the
// processed-with-errors folder its file is kept in.
const outcomeOf = (h) => (h.status === 'loaded' && h.counts.invalid > 0 ? 'loaded_errors' : h.status);
const outcomeInfo = (value) => OUTCOMES.find(o => o.value === value) || { label: value, cls: '' };
const localDate = (stamp) => new Date(stamp).toLocaleDateString('en-CA'); // YYYY-MM-DD

// A symbol that opens its chart. Stops the click so a history row doesn't also open
// its detail; a symbol that couldn't be read from the file stays plain text.
function SymbolLink({ symbol, onNavigate }) {
  if (!symbol || !onNavigate) return <strong className="mono">{symbol}</strong>;
  return (
    <strong className="mono" style={{ cursor: 'pointer', color: 'var(--accent)' }} title={`Chart ${symbol}`}
      onClick={e => { e.stopPropagation(); onNavigate('charts', { symbol }); }}>{symbol}</strong>
  );
}

// Where an import is in the stage -> check -> load pipeline, worked out from the
// batch as it stands (it keeps a status, not a per-stage history). The folder its
// file is kept in says whether it has been filed away; a rejected file has no
// staged rows, which is how a failed check is told apart from a failed load.
const STAGE_STATES = { done: 'Done', error: 'Problem', skipped: 'Skipped', next: 'Next step', pending: 'Not reached' };
const FILED_FOLDERS = { processed: 'done', 'processed-with-errors': 'error', failed: 'error', discarded: 'skipped' };

function lifecycleStages(d) {
  const at = (stamp) => (stamp ? ` ${new Date(stamp).toLocaleString()}` : '');
  const folder = d.stored_path ? d.stored_path.split('/')[0] : null;
  const rejected = d.status === 'failed' && d.row_count === 0;
  const loadFailed = d.status === 'failed' && !rejected;

  const received = folder
    ? { state: 'done', note: `File saved on the NAS${at(d.created_at)}` }
    : { state: 'pending', note: 'No copy of the file was kept' };

  const checked = rejected
    ? { state: 'error', note: d.error || 'The file was rejected' }
    : { state: 'done', note: `${d.row_count} rows staged and checked${at(d.created_at)}` };

  // Transaction files only: every sell's lots are confirmed before the batch can load.
  const isTransactions = d.data_type === 'transactions';
  let lots = null;
  if (isTransactions) {
    const sells = d.counts.sells || 0;
    if (d.status === 'staged') {
      if (d.counts.lot_problems > 0) lots = { state: 'error', note: `${d.counts.lot_problems} sells sell more shares than the account holds` };
      else if (d.counts.sells_unconfirmed > 0) lots = { state: 'next', note: `Confirm the lots for ${d.counts.sells_unconfirmed} of ${sells} sells` };
      else lots = { state: 'done', note: sells ? `Lots confirmed for all ${sells} sells` : 'No sells in this file' };
    } else if (d.status === 'loaded') {
      lots = { state: 'done', note: sells ? `Lots recorded for ${sells} sells` : 'No sells in this file' };
    } else if (d.status === 'discarded') {
      lots = { state: 'skipped', note: 'Discarded before loading' };
    } else {
      lots = { state: rejected ? 'pending' : 'error', note: rejected ? 'Not reached' : 'The load failed' };
    }
  }
  const lotsReady = !lots || lots.state === 'done';

  let loaded;
  if (d.status === 'staged' && !lotsReady) {
    loaded = { state: 'pending', note: 'Waiting for the lots to be confirmed' };
  } else if (d.status === 'staged') {
    loaded = { state: 'next', note: d.counts.to_load === 0 ? 'Nothing new to load' : `Load ${d.counts.to_load} rows` };
  } else if (d.status === 'loaded') {
    loaded = d.counts.invalid > 0
      ? { state: 'error', note: `${d.loaded_count} rows loaded${at(d.loaded_at)}; ${d.counts.invalid} invalid rows left out` }
      : { state: 'done', note: `${d.loaded_count} rows loaded${at(d.loaded_at)}` };
  } else if (loadFailed) {
    loaded = { state: 'error', note: d.error || 'The load failed' };
  } else if (d.status === 'discarded') {
    loaded = { state: 'skipped', label: 'Discarded', note: 'Discarded without loading' };
  } else {
    loaded = { state: 'pending', note: 'Not loaded' };
  }

  let filed;
  if (!folder) filed = { state: 'pending', note: 'No copy of the file was kept' };
  else if (FILED_FOLDERS[folder]) filed = { state: FILED_FOLDERS[folder], note: `Kept in ${folder}/` };
  else if (d.status === 'staged') filed = { state: 'pending', note: `In ${folder}/ until it is loaded or discarded` };
  else filed = { state: 'error', note: `Still in ${folder}/: moving it to its outcome folder failed` };

  return [
    { key: 'received', label: 'Received', ...received },
    { key: 'checked', label: 'Checked', ...checked },
    ...(lots ? [{ key: 'lots', label: 'Lots', ...lots }] : []),
    { key: 'loaded', label: 'Loaded', ...loaded },
    { key: 'filed', label: 'Filed', ...filed },
  ];
}

// The lifecycle as a row of stage buttons. The Loaded stage of an import waiting
// to load runs the load; the others only describe their stage on hover.
function ImportLifecycle({ detail, onLoad, busy }) {
  const stages = lifecycleStages(detail);
  return (
    <div className="lifecycle">
      {stages.map((s, i) => {
        const actionable = s.key === 'loaded' && s.state === 'next' && detail.counts.to_load > 0 && !busy;
        return (
          <React.Fragment key={s.key}>
            {i > 0 && <span className="lifecycle-arrow" aria-hidden="true">→</span>}
            <button type="button"
              className={`lifecycle-stage ${s.state}${actionable ? ' actionable' : ''}`}
              title={`${STAGE_STATES[s.state]}: ${s.note}`}
              aria-disabled={!actionable}
              onClick={actionable ? onLoad : undefined}>
              {s.label}
            </button>
          </React.Fragment>
        );
      })}
    </div>
  );
}

// What an import is for: its symbol, or for a transaction file with several
// symbols, the account it loads into.
function ImportSubject({ item, accountNames, onNavigate }) {
  if (!item.symbol && item.data_type === 'transactions') {
    return <strong>{accountNames[item.account_id] || 'Account'}</strong>;
  }
  return <SymbolLink symbol={item.symbol} onNavigate={onNavigate} />;
}

// Home for data operations: loading history and transaction files, with room for
// intraday and export features as they are built.
export default function Data({ onNavigate }) {
  const [imports, setImports] = useState(null);
  const [mmSymbols, setMmSymbols] = useState([]);
  const [accountNames, setAccountNames] = useState({});
  const [filterSymbol, setFilterSymbol] = useState('');
  const [filterAccount, setFilterAccount] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterOutcome, setFilterOutcome] = useState('');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [detail, setDetail] = useState(null);
  const [detailBusy, setDetailBusy] = useState(false);
  const [detailError, setDetailError] = useState('');

  const load = () => {
    api.getImports().then(rows => setImports(rows || [])).catch(() => setImports([]));
    api.getMoneyMarketSymbols().then(rows => setMmSymbols(rows || [])).catch(() => {});
    api.getAccounts().then(rows => setAccountNames(Object.fromEntries((rows || []).map(a => [a.id, a.name])))).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  useRefreshOnFocus(load);

  const rows = (imports || []).map(h => ({
    ...h,
    outcome: outcomeOf(h),
    loaded: h.loaded_count,
    duplicate: h.counts.duplicate,
    invalid: h.counts.invalid,
  }));
  const symbols = [...new Set(rows.map(h => h.symbol).filter(Boolean))].sort();

  const filtered = rows.filter(h =>
    (!filterSymbol || h.symbol === filterSymbol)
    && (!filterAccount || String(h.account_id) === filterAccount)
    && (!filterType || h.data_type === filterType)
    && (!filterOutcome || h.outcome === filterOutcome)
    && (!filterFrom || localDate(h.created_at) >= filterFrom)
    && (!filterTo || localDate(h.created_at) <= filterTo));

  const sorted = [...filtered].sort((a, b) => {
    const av = a[sortField] ?? '', bv = b[sortField] ?? '';
    if (typeof av === 'string' || typeof bv === 'string') {
      return sortDir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    }
    return sortDir === 'asc' ? av - bv : bv - av;
  });

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const SortTh = ({ field, label, right }) => (
    <th onClick={() => toggleSort(field)} style={{ cursor: 'pointer', userSelect: 'none', textAlign: right ? 'right' : undefined }}>
      {label} {sortField === field ? (sortDir === 'asc' ? '↑' : '↓') : ''}
    </th>
  );

  const accountsUsed = [...new Set(rows.map(h => h.account_id).filter(Boolean))]
    .sort((a, b) => String(accountNames[a] || '').localeCompare(String(accountNames[b] || '')));
  const anyFilter = filterSymbol || filterAccount || filterType || filterOutcome || filterFrom || filterTo;
  const clearFilters = () => {
    setFilterSymbol(''); setFilterAccount(''); setFilterType(''); setFilterOutcome(''); setFilterFrom(''); setFilterTo('');
  };

  const openDetail = async (id) => {
    setDetailError('');
    try { setDetail(await api.getImport(id)); } catch (e) { window.alert(e.message); }
  };

  const loadDetail = async () => {
    setDetailBusy(true);
    setDetailError('');
    try { setDetail(await api.loadImport(detail.id)); } catch (e) { setDetailError(e.message); }
    setDetailBusy(false);
    load();
  };

  const discardDetail = async () => {
    setDetailBusy(true);
    setDetailError('');
    try {
      await api.discardImport(detail.id);
      setDetail(null);
    } catch (e) {
      setDetailError(e.message);
    }
    setDetailBusy(false);
    load();
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Data</h1>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 20, marginBottom: 20, alignItems: 'start' }}>
        <HistoryImport moneyMarketSymbols={mmSymbols} onChanged={load} />

        <div className="card">
          <div className="modal-title">Intraday Data</div>
          <div className="muted" style={{ fontSize: 13 }}>Loading intraday price history from a file is not available yet.</div>
        </div>
      </div>

      <div style={{ marginBottom: 20 }}>
        <TransactionImport onChanged={load} />
      </div>

      <div className="card">
        <div className="modal-title">Import History</div>
        <div className="muted" style={{ fontSize: 13, marginBottom: 16 }}>
          Every data file you have imported. Click a row for its details.
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          <select style={{ width: 'auto' }} value={filterSymbol} onChange={e => setFilterSymbol(e.target.value)}>
            <option value="">All Symbols</option>
            {symbols.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          {accountsUsed.length > 0 && (
            <select style={{ width: 'auto' }} value={filterAccount} onChange={e => setFilterAccount(e.target.value)}>
              <option value="">All Accounts</option>
              {accountsUsed.map(id => <option key={id} value={String(id)}>{accountNames[id] || `Account ${id}`}</option>)}
            </select>
          )}
          <select style={{ width: 'auto' }} value={filterType} onChange={e => setFilterType(e.target.value)}>
            <option value="">All Types</option>
            {Object.entries(TYPE_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
          <select style={{ width: 'auto' }} value={filterOutcome} onChange={e => setFilterOutcome(e.target.value)}>
            <option value="">All Outcomes</option>
            {OUTCOMES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <span className="muted" style={{ fontSize: 13 }}>Imported</span>
          <input type="date" style={{ width: 'auto' }} value={filterFrom} onChange={e => setFilterFrom(e.target.value)} title="Imported on or after" aria-label="Imported on or after" />
          <span className="muted" style={{ fontSize: 13 }}>to</span>
          <input type="date" style={{ width: 'auto' }} value={filterTo} onChange={e => setFilterTo(e.target.value)} title="Imported on or before" aria-label="Imported on or before" />
          {anyFilter && <button className="btn-ghost" onClick={clearFilters}>Clear Filters</button>}
          {imports !== null && (
            <span className="muted" style={{ fontSize: 13, marginLeft: 'auto' }}>{sorted.length} of {rows.length}</span>
          )}
        </div>

        {imports === null ? <div className="muted" style={{ padding: 32, textAlign: 'center' }}>Loading...</div> : (
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <SortTh field="created_at" label="Imported" />
                  <SortTh field="symbol" label="Symbol" />
                  <SortTh field="data_type" label="Type" />
                  <SortTh field="outcome" label="Outcome" />
                  <SortTh field="first_date" label="Dates Covered" />
                  <SortTh field="loaded" label="Loaded" right />
                  <SortTh field="duplicate" label="Duplicates" right />
                  <SortTh field="invalid" label="Invalid" right />
                  <SortTh field="source" label="Via" />
                  <SortTh field="filename" label="File" />
                </tr>
              </thead>
              <tbody>
                {sorted.map(h => {
                  const o = outcomeInfo(h.outcome);
                  return (
                    <tr key={h.id} onClick={() => openDetail(h.id)} style={{ cursor: 'pointer' }} title="View import details">
                      <td className="muted" style={{ whiteSpace: 'nowrap' }}>{new Date(h.created_at).toLocaleString()}</td>
                      <td><ImportSubject item={h} accountNames={accountNames} onNavigate={onNavigate} /></td>
                      <td>{TYPE_LABELS[h.data_type] || h.data_type}</td>
                      <td className={o.cls} style={{ whiteSpace: 'nowrap' }} title={h.error || undefined}>{o.label}</td>
                      <td className="mono muted" style={{ whiteSpace: 'nowrap' }}>{h.first_date ? `${h.first_date} to ${h.last_date}` : '—'}</td>
                      <td className="mono amount">{h.loaded}</td>
                      <td className="mono amount">{h.duplicate}</td>
                      <td className={`mono amount ${h.invalid ? 'loss' : ''}`}>{h.invalid}</td>
                      <td className="muted">{SOURCE_LABELS[h.source] || h.source}</td>
                      <td className="muted" style={{ fontSize: 12 }}>{h.filename}</td>
                    </tr>
                  );
                })}
                {sorted.length === 0 && (
                  <tr><td colSpan={10} className="muted" style={{ textAlign: 'center', padding: 32 }}>
                    {rows.length === 0 ? 'No files have been imported yet.' : 'No imports match these filters.'}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {detail && (
        <div className="modal-overlay" onClick={() => setDetail(null)}>
          <div className="modal" style={{ maxWidth: detail.data_type === 'transactions' ? 1040 : 720 }} onClick={e => e.stopPropagation()}>
            <div className="modal-title">
              <ImportSubject item={detail} accountNames={accountNames} onNavigate={onNavigate} /> · {TYPE_LABELS[detail.data_type] || detail.data_type} · <span className={outcomeInfo(outcomeOf(detail)).cls}>{outcomeInfo(outcomeOf(detail)).label}</span>
            </div>
            <ImportLifecycle detail={detail} onLoad={loadDetail} busy={detailBusy} />
            <div className="muted" style={{ fontSize: 13, marginBottom: 12 }}>
              <div>{detail.filename}</div>
              {detail.account_name && <div>Loads into {detail.account_name}</div>}
              <div>Imported {new Date(detail.created_at).toLocaleString()} via {(SOURCE_LABELS[detail.source] || detail.source).toLowerCase()}</div>
              {detail.first_date && <div>{detail.row_count} rows, {detail.first_date} to {detail.last_date}</div>}
              {detail.stored_path && <div>Kept on the NAS at <span className="mono">{detail.stored_path}</span></div>}
            </div>

            {detail.data_type === 'transactions' ? (
              <TransactionBatchView batch={detail} showHeader={false}
                onBatch={setDetail} onChanged={load} onDiscarded={() => setDetail(null)} />
            ) : (
              <>
                {detail.error && <div className="loss" style={{ fontSize: 13, marginBottom: 12 }}>{detail.error}</div>}
                {detail.warnings.map((w, i) => (
                  <div key={i} className="loss" style={{ fontSize: 13, marginBottom: 8 }}>{w}</div>
                ))}

                {detail.row_count > 0 && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 12 }}>
                    {[
                      detail.status === 'loaded' ? ['Loaded', detail.counts.loaded] : ['To load', detail.counts.to_load],
                      ['Duplicates', detail.counts.duplicate],
                      ['Invalid', detail.counts.invalid],
                      ['Partial day', detail.counts.partial_day],
                    ].map(([label, n]) => (
                      <div key={label} className="stat-card" style={{ textAlign: 'right' }}>
                        <div className="stat-label">{label}</div>
                        <div className="stat-value mono" style={{ fontSize: 16 }}>{n}</div>
                      </div>
                    ))}
                  </div>
                )}

                {detail.counts.duplicate > 0 && (
                  <div className="muted" style={{ fontSize: 13, marginBottom: 8 }}>
                    {detail.counts.duplicate_differing > 0
                      ? `${detail.counts.duplicate_differing} of the duplicates differ from the stored values (listed below).`
                      : 'All duplicates match the stored values.'}
                  </div>
                )}

                {detail.attention_rows.length > 0 && (
                  <div style={{ maxHeight: 280, overflowY: 'auto', marginBottom: 12 }}>
                    <table>
                      <thead>
                        <tr><th>Row</th><th>Date</th><th>Status</th><th>Notes</th></tr>
                      </thead>
                      <tbody>
                        {detail.attention_rows.map(r => (
                          <tr key={r.row_number}>
                            <td className="mono muted">{r.row_number}</td>
                            <td className="mono">{r.date || (r.raw && r.raw.date) || '—'}</td>
                            <td className={r.status === 'invalid' ? 'loss' : ''} style={{ whiteSpace: 'nowrap' }}>{STATUS_LABELS[r.status] || r.status}</td>
                            <td className="muted" style={{ fontSize: 12 }}>{r.notes}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {detail.attention_rows_total > detail.attention_rows.length && (
                      <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
                        Showing {detail.attention_rows.length} of {detail.attention_rows_total} rows.
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {detailError && <div className="loss" style={{ fontSize: 13, marginBottom: 12 }}>{detailError}</div>}

            <div className="modal-actions">
              {detail.status === 'staged' && detail.data_type !== 'transactions' && (
                <>
                  <button className="btn-ghost" onClick={discardDetail} disabled={detailBusy}>Discard</button>
                  <button className="btn" onClick={loadDetail} disabled={detailBusy || detail.counts.to_load === 0}>
                    {detail.counts.to_load === 0 ? 'Nothing new to load' : `Load ${detail.counts.to_load} rows`}
                  </button>
                </>
              )}
              <button className="btn-ghost" onClick={() => setDetail(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
